import { fluidProp } from 'remote-com-shared/style/helpers';
import styled, { css } from 'styled-components';
import { LinkButton } from '@/src/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import BackgroundImage from './assets/contact-us-background.svg';
import { opacify, rgba } from 'polished';
import { themeColorTones } from 'remote-com-shared/style/colors';

const backgroundSeconaryURL = '/images/illustrations/contact-us-background-secondary.svg';

const Label = styled.div`
  margin: 0 auto 18px auto;
  text-align: center;

  & > small {
    ${({ theme }) => theme.typography.subHead}
    text-transform: uppercase;
    color: var(--colors-turquoise);
  }
`;

const Title = styled.h1`
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '24px', '42px')}
    color: var(--colors-blank);
  margin: 0 auto;

  // Fallback + new way
  white-space: pre-wrap;
  white-space: break-spaces;
`;

const Description = styled.p`
  ${({ theme }) => theme.typography.bodyLead}
  margin: 18px auto;
  color: var(--colors-moonRaker);

  ${({ $wideContent }) => {
    switch ($wideContent) {
      case true:
        return css`
          max-width: 750px;
        `;
      case false:
      default:
        return css`
          max-width: 690px;
        `;
    }
  }}
`;

const SectionWrapper = styled.section`
  ${fluidProp(['padding-top', 'padding-bottom'], '150px', '130px')}
  ${fluidProp(['padding-left', 'padding-right'], '16px', '48px')}

  position: relative;
  text-align: center;
  background-color: var(--color-tone-primary, var(--newBrandColor));
`;
const SectionWrapperSecondary = styled(SectionWrapper)`
  font-family: 'Bossa', sans-serif;
`;

const Container = styled(ContentContainer)`
  position: relative;
  z-index: 1;
`;

const IllustrationWrapper = styled.div`
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 0;
  overflow: hidden;

  svg {
    width: 100vw;
    height: 100%;

    ${({ theme: { colorTone } }) => {
      // for "darkBlue-mint", the "mint" needs 0.3 opacity
      if (colorTone === 'darkBlue-mint') {
        return css`
          color: ${rgba(themeColorTones[colorTone].secondary, 0.3)};
        `;
      }

      return css`
        color: var(--color-tone-secondary, #0261fe);
      `;
    }}
  }
`;

const BackgroundImageSecondary = styled.div`
  width: 100vw;
  height: 100%;

  background-image: url(${staticAssetUrl(backgroundSeconaryURL)});
  background-size: cover;
  background-position: center;
`;

const CTAButton = styled(LinkButton).attrs({
  withBackground: true,
})`
  && {
    ${({ theme }) => theme.typography.bodyLeadMedium}
    ${fluidProp('margin-top', '20px', '30px')}
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'secondary':
        return css`
          color: var(--colors-blank);
          border: 1px solid var(--colors-blank);

          &:hover {
            background-color: var(--colors-blank);
            color: var(--colors-irisBlue);
          }
        `;

      default:
        return css`
          background-color: var(--colors-blank);
          color: var(--colors-darkBlue);

          &:hover {
            color: var(--colors-irisBlue);
          }
        `;
    }
  }}
`;

export const PrimaryCTAButton = ({ children, ...props }) => (
  <CTAButton {...props} label={children} />
);
export const CTAButtonSecondary = ({ children, ...props }) => (
  <CTAButton {...props} label={children} $variant="secondary" />
);

export const PrimaryCTA = ({
  title = 'Discover how Remote can make international hiring easier.',
  description,
  label,
  children,
  button,
  ...props
}) => (
  <SectionWrapper {...props}>
    <Container>
      {label && (
        <Label>
          <small>{label}</small>
        </Label>
      )}
      <Title>{title}</Title>
      {description && <Description>{description}</Description>}
      {children}
    </Container>
    <IllustrationWrapper>
      <BackgroundImage />
    </IllustrationWrapper>
  </SectionWrapper>
);

export const CTASectionSecondary = ({
  title = <>Discover how Remote can help you grow your global team.</>,
  description,
  label,
  children,
  button,
  ...props
}) => (
  <SectionWrapperSecondary {...props}>
    <Container>
      {label && (
        <Label>
          <small>{label}</small>
        </Label>
      )}
      <Title>{title}</Title>
      {description && <Description>{description}</Description>}
      {children}
    </Container>
    <IllustrationWrapper>
      <BackgroundImageSecondary />
    </IllustrationWrapper>
  </SectionWrapperSecondary>
);
